import { createServerFn } from "@tanstack/react-start";
import { and, eq, inArray, isNotNull } from "drizzle-orm";
import { z } from "zod";
import type { Author } from "~/lib/citations/identity-resolution";
import { db } from "~/lib/db";
import { projectCitations, projectScreeningSettings } from "~/lib/db/schema";
import { requireProjectAccess, requireUser } from "~/lib/server/helpers.server";
import { detectDuplicatesForProject } from "./detection";

const projectIdSchema = z.object({
  projectId: z.string(),
});

const groupDetailSchema = z.object({
  projectId: z.string(),
  groupId: z.string(),
});

const resolveDuplicateSchema = z.object({
  projectId: z.string(),
  groupId: z.string(),
  action: z.enum(["confirm_duplicates", "mark_unique"]),
  masterId: z.string().optional(),
});

export interface DuplicateGroup {
  groupId: string;
  citationCount: number;
  sampleTitle: string;
  status: "pending" | "resolved";
}

export interface DuplicateGroupCitation {
  id: string;
  title: string;
  authors: Author[] | null;
  year: number | null;
  containerTitle: string | null;
  doi: string | null;
  pmid: string | null;
  abstract: string | null;
  volume: string | null;
  issue: string | null;
  pages: string | null;
  duplicateStatus:
    | "unique"
    | "potential_duplicate"
    | "confirmed_duplicate"
    | "master"
    | null;
  sourceName: string | null;
}

export const $getDuplicateGroups = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => projectIdSchema.parse(data))
  .handler(async ({ data }) => {
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);

    // Get all citations with duplicate group IDs
    const citations = await db.query.projectCitations.findMany({
      where: and(
        eq(projectCitations.projectId, data.projectId),
        isNotNull(projectCitations.duplicateGroupId),
      ),
      with: {
        canonicalReference: true,
      },
    });

    // Group by duplicateGroupId
    const groupMap = new Map<
      string,
      {
        citations: typeof citations;
        hasUnresolved: boolean;
      }
    >();

    for (const citation of citations) {
      const groupId = citation.duplicateGroupId!;
      if (!groupMap.has(groupId)) {
        groupMap.set(groupId, { citations: [], hasUnresolved: false });
      }
      const group = groupMap.get(groupId)!;
      group.citations.push(citation);
      if (citation.duplicateStatus === "potential_duplicate") {
        group.hasUnresolved = true;
      }
    }

    // Convert to DuplicateGroup array
    const groups: DuplicateGroup[] = [];
    for (const [groupId, { citations: groupCitations, hasUnresolved }] of groupMap) {
      groups.push({
        groupId,
        citationCount: groupCitations.length,
        sampleTitle: groupCitations[0]?.canonicalReference.title ?? "Unknown",
        status: hasUnresolved ? "pending" : "resolved",
      });
    }

    // Sort: pending first, then by citation count descending
    groups.sort((a, b) => {
      if (a.status !== b.status) {
        return a.status === "pending" ? -1 : 1;
      }
      return b.citationCount - a.citationCount;
    });

    return groups;
  });

export const $getDuplicateGroupDetail = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => groupDetailSchema.parse(data))
  .handler(async ({ data }) => {
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);

    const citations = await db.query.projectCitations.findMany({
      where: and(
        eq(projectCitations.projectId, data.projectId),
        eq(projectCitations.duplicateGroupId, data.groupId),
      ),
      with: {
        canonicalReference: {
          with: {
            sourceLinks: {
              with: {
                sourceRecord: true,
              },
              limit: 1,
            },
          },
        },
      },
    });

    if (citations.length === 0) {
      throw new Error("Duplicate group not found");
    }

    return citations.map((c) => ({
      id: c.id,
      title: c.canonicalReference.title,
      authors: c.canonicalReference.authors as Author[] | null,
      year: c.canonicalReference.year,
      containerTitle: c.canonicalReference.containerTitle,
      doi: c.canonicalReference.doi,
      pmid: c.canonicalReference.pmid,
      abstract: c.canonicalReference.abstract,
      volume: c.canonicalReference.volume,
      issue: c.canonicalReference.issue,
      pages: c.canonicalReference.pages,
      duplicateStatus: c.duplicateStatus,
      sourceName: c.canonicalReference.sourceLinks[0]?.sourceRecord?.sourceName ?? null,
    })) satisfies DuplicateGroupCitation[];
  });

export const $resolveDuplicateGroup = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => resolveDuplicateSchema.parse(data))
  .handler(async ({ data }) => {
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);

    // Get all citations in the group
    const citations = await db.query.projectCitations.findMany({
      where: and(
        eq(projectCitations.projectId, data.projectId),
        eq(projectCitations.duplicateGroupId, data.groupId),
      ),
    });

    if (citations.length === 0) {
      throw new Error("Duplicate group not found");
    }

    if (data.action === "confirm_duplicates") {
      if (!data.masterId) {
        throw new Error("Master citation ID required for confirming duplicates");
      }

      // Verify master is in the group
      const masterCitation = citations.find((c) => c.id === data.masterId);
      if (!masterCitation) {
        throw new Error("Master citation not found in group");
      }

      // Update master citation
      await db
        .update(projectCitations)
        .set({
          duplicateStatus: "master",
          // Keep duplicateGroupId so we can track resolved groups
          screenedBy: user.id,
          screenedAt: new Date(),
        })
        .where(eq(projectCitations.id, data.masterId));

      // Update other citations as confirmed duplicates
      const otherIds = citations.filter((c) => c.id !== data.masterId).map((c) => c.id);
      if (otherIds.length > 0) {
        await db
          .update(projectCitations)
          .set({
            duplicateStatus: "confirmed_duplicate",
            // Keep duplicateGroupId so we can track resolved groups
            screeningStatus: "excluded",
            exclusionReason: "Duplicate",
            exclusionCode: "duplicate",
            screenedBy: user.id,
            screenedAt: new Date(),
          })
          .where(
            and(
              eq(projectCitations.projectId, data.projectId),
              inArray(projectCitations.id, otherIds),
            ),
          );
      }
    } else if (data.action === "mark_unique") {
      // Mark all as unique
      await db
        .update(projectCitations)
        .set({
          duplicateStatus: "unique",
          duplicateGroupId: null,
          screenedBy: user.id,
          screenedAt: new Date(),
        })
        .where(
          and(
            eq(projectCitations.projectId, data.projectId),
            eq(projectCitations.duplicateGroupId, data.groupId),
          ),
        );
    }

    return { success: true };
  });

export const $detectDuplicates = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => projectIdSchema.parse(data))
  .handler(async ({ data }) => {
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);

    // Fetch deduplication mode from settings
    const settings = await db.query.projectScreeningSettings.findFirst({
      where: eq(projectScreeningSettings.projectId, data.projectId),
    });
    const dedupMode = (settings?.deduplicationMode === "manual" ? "manual" : "automatic") as "automatic" | "manual";

    const result = await detectDuplicatesForProject(data.projectId, dedupMode);

    return {
      groupsCreated: result.groupsCreated,
      citationsProcessed: result.citationsProcessed,
      duplicatesRemoved: result.duplicatesRemoved,
      mode: dedupMode,
    };
  });
